import dayjs, { Dayjs } from "dayjs";
import ILeadData from "../../models/LeadData.model";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import "react-datepicker/dist/react-datepicker.css";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

dayjs.extend(utc);
dayjs.extend(timezone);

interface AddCallRemarkModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleSaveRemark?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  remark?: string;
  setRemark?: (remark: string) => void;
  leadInfo: ILeadData;
  showOnDate?: Date | null;
  setShowOnDate?: (date: Date | null) => void;
}

const AddCallRemarkModal: React.FC<AddCallRemarkModalProps> = ({
  isOpen,
  setIsOpen,
  handleSaveRemark,
  leadInfo,
  isLoading = false,
  remark = "",
  setRemark = () => {},
  showOnDate,
  setShowOnDate = () => {},
}) => {
  const [dateValue, setDateValue] = useState<Dayjs | null>(
    showOnDate ? dayjs(showOnDate) : null
  );

  const addingCallRemarksLoading = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.addingCallRemarks
  );
  const [timeValue, setTimeValue] = useState<Dayjs | null>(
    showOnDate ? dayjs(showOnDate) : null
  );

  useEffect(() => {
    const handleDateTimeChange = () => {
      if (dateValue && timeValue) {
        const combinedDate = dateValue
          .set("hour", timeValue.hour())
          .set("minute", timeValue.minute())
          .toDate();
        setShowOnDate(combinedDate);
      } else if (dateValue) {
        setShowOnDate(dateValue.startOf("day").toDate());
      } else {
        setShowOnDate(null);
      }
    };
    handleDateTimeChange();
  }, [dateValue, setShowOnDate, timeValue]);
  const handleClose = () => {
    setIsOpen(false);
    if (localStorage.getItem("isModalOpen") === "true") {
      localStorage.removeItem("isModalOpen");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300" />
      <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-md mx-auto transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <svg
                className="h-4 w-4 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              Add Call Remark
            </h3>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
            aria-label="Close modal"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 px-6 py-4 bg-slate-50/50 border-b border-slate-200">
          <DatePicker
            value={dateValue}
            onChange={(newValue) => setDateValue(newValue)}
            minDate={dayjs()}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
              },
            }}
          />
          <TimePicker
            value={timeValue}
            onChange={(newValue) => setTimeValue(newValue)}
            ampm={true}
            format="hh:mm A"
            views={["hours", "minutes"]}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
              },
            }}
          />
        </div>

        {/* Lead Information */}
        <div className="px-6 py-4 bg-slate-50/50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {leadInfo?.name?.charAt(0)?.toUpperCase() || "L"}
                </span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">
                  {leadInfo?.name || "Unknown Lead"}
                </h4>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{leadInfo?.mobileNumber || "No phone number"}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                {leadInfo?.status || "Unknown"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {leadInfo?.type || "Lead"}
              </div>
            </div>
          </div>
        </div>

        {/* Remark Input */}
        <div className="px-6 py-6">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Call Remark
            </label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter your call remarks here... (e.g., Customer response, follow-up needed, etc.)"
              rows={4}
              disabled={isLoading}
              className="w-full px-3.5 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none transition-all duration-200 text-slate-900 placeholder-slate-400 disabled:bg-slate-50 disabled:cursor-not-allowed text-sm leading-relaxed"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50/50 border-t border-slate-200 rounded-b-xl">
          <button
            onClick={handleSaveRemark}
            disabled={addingCallRemarksLoading || !remark.trim()}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[110px] justify-center shadow-sm"
          >
            {addingCallRemarksLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Remark
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCallRemarkModal;
